import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Dimensions,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { markTutorialComplete } from '../../services/tutorial-service';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';

const { width, height } = Dimensions.get('window');

interface HostRevenueSplitTutorialProps {
  visible: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

const tutorialSteps = [
  {
    id: 1,
    title: 'Welcome to Revenue Splits!',
    icon: 'people-outline' as const,
    description: 'Share your charger revenue with partners easily and transparently.',
    details: [
      'Designate partners to receive a percentage of your earnings',
      'Track splits across all your chargers',
      'See real-time analytics and reports',
    ],
  },
  {
    id: 2,
    title: 'Adding Revenue Partners',
    icon: 'add-circle-outline' as const,
    description: 'Set up revenue splits when registering or editing a charger.',
    details: [
      'Click the + button to add a partner',
      'Enter their email and split percentage (0.01-100%)',
      'Add multiple partners if needed',
      'Total splits cannot exceed 100%',
    ],
  },
  {
    id: 3,
    title: 'Managing Your Splits',
    icon: 'settings-outline' as const,
    description: 'Keep track of your revenue sharing arrangements.',
    details: [
      'View your effective rate after splits',
      'See partner payout status',
      'Copy splits from one charger to another',
      'Update or revoke splits anytime',
    ],
  },
  {
    id: 4,
    title: 'Dashboard & Analytics',
    icon: 'analytics-outline' as const,
    description: 'Monitor your shared revenue performance.',
    details: [
      'View total shared revenue this month',
      'See top partners by earnings',
      'Track most profitable shared chargers',
      'Compare gross vs. net earnings',
    ],
  },
  {
    id: 5,
    title: 'Charger Cards & History',
    icon: 'card-outline' as const,
    description: 'Revenue split indicators throughout the app.',
    details: [
      'Chargers with splits show a people icon badge',
      'View gross revenue, splits, and net revenue',
      'See split breakdowns in charge history',
      'Track month-to-date split deductions',
    ],
  },
];

const HostRevenueSplitTutorial: React.FC<HostRevenueSplitTutorialProps> = ({
  visible,
  onComplete,
  onSkip,
}) => {
  const user = useSelector((state: RootState) => state.user.userObj);
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      await markTutorialComplete('HOST_REVENUE_SPLIT');
    } catch (error) {
      console.error('Error marking tutorial as complete:', error);
    }
    onComplete();
  };

  const handleSkip = async () => {
    try {
      await markTutorialComplete('HOST_REVENUE_SPLIT');
    } catch (error) {
      console.error('Error marking tutorial as complete:', error);
    }
    onSkip();
  };

  const step = tutorialSteps[currentStep];
  const progress = ((currentStep + 1) / tutorialSteps.length) * 100;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={handleSkip}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Revenue Split Tutorial</Text>
            <TouchableOpacity onPress={handleSkip} style={styles.skipButton}>
              <Text style={styles.skipButtonText}>Skip</Text>
            </TouchableOpacity>
          </View>

          {/* Progress Bar */}
          <View style={styles.progressContainer}>
            <View style={[styles.progressBar, { width: `${progress}%` }]} />
          </View>

          {/* Content */}
          <ScrollView
            style={styles.content}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.iconContainer}>
              <Ionicons name={step.icon} size={64} color="#3B82F6" />
            </View>

            <Text style={styles.stepTitle}>{step.title}</Text>
            <Text style={styles.stepDescription}>{step.description}</Text>

            <View style={styles.detailsContainer}>
              {step.details.map((detail, index) => (
                <View key={index} style={styles.detailItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#51B868" />
                  <Text style={styles.detailText}>{detail}</Text>
                </View>
              ))}
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity
              onPress={handlePrevious}
              style={[
                styles.button,
                styles.secondaryButton,
                currentStep === 0 && styles.buttonDisabled,
              ]}
              disabled={currentStep === 0}
            >
              <Ionicons
                name="arrow-back"
                size={20}
                color={currentStep === 0 ? '#8D8E90' : '#3B82F6'}
              />
              <Text
                style={[
                  styles.buttonText,
                  styles.secondaryButtonText,
                  currentStep === 0 && styles.buttonTextDisabled,
                ]}
              >
                Previous
              </Text>
            </TouchableOpacity>

            <View style={styles.stepIndicator}>
              <Text style={styles.stepIndicatorText}>
                {currentStep + 1} / {tutorialSteps.length}
              </Text>
            </View>

            <TouchableOpacity
              onPress={handleNext}
              style={[styles.button, styles.primaryButton]}
            >
              <Text style={[styles.buttonText, styles.primaryButtonText]}>
                {currentStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}
              </Text>
              <Ionicons name="arrow-forward" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#292D33',
    borderRadius: 20,
    width: Math.min(width - 40, 500),
    maxHeight: height - 100,
    minHeight: 400,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#1A1D23',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  skipButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  skipButtonText: {
    color: '#8D8E90',
    fontSize: 14,
    fontWeight: '600',
  },
  progressContainer: {
    height: 4,
    backgroundColor: '#1A1D23',
  },
  progressBar: {
    height: '100%',
    backgroundColor: '#3B82F6',
  },
  content: {
    flexGrow: 1,
    minHeight: 300,
  },
  contentContainer: {
    padding: 24,
    alignItems: 'center',
    flexGrow: 1,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 24,
  },
  stepTitle: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  stepDescription: {
    color: '#E8E8E9',
    fontSize: 16,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  detailsContainer: {
    width: '100%',
    gap: 12,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  detailText: {
    color: '#E8E8E9',
    fontSize: 14,
    flex: 1,
    lineHeight: 20,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#1A1D23',
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  primaryButton: {
    backgroundColor: '#3B82F6',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  buttonDisabled: {
    borderColor: '#8D8E90',
    opacity: 0.5,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  primaryButtonText: {
    color: '#fff',
  },
  secondaryButtonText: {
    color: '#3B82F6',
  },
  buttonTextDisabled: {
    color: '#8D8E90',
  },
  stepIndicator: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#1A1D23',
    borderRadius: 12,
  },
  stepIndicatorText: {
    color: '#E8E8E9',
    fontSize: 12,
    fontWeight: '600',
  },
});

export default HostRevenueSplitTutorial;

